/**
 * Onboarding Org Detail Page
 *
 * Shows full onboarding detail for a single organization.
 * Reached by clicking an org row in the step drill-down.
 */

import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { founderDashboardAPI } from '../../../services/api';
import { useEnvironment } from '../../../context/EnvironmentContext';
import KPICard from '../../../components/founder-dashboard/KPICard';
import RefreshIndicator from '../../../components/founder-dashboard/RefreshIndicator';
import {
  ArrowLeftIcon,
  BuildingOffice2Icon,
  MapPinIcon,
  CalendarIcon,
  UsersIcon,
  UserIcon,
  ChevronRightIcon,
  ArrowTopRightOnSquareIcon,
} from '@heroicons/react/24/outline';
import {
  CheckCircleIcon as CheckCircleSolidIcon,
  XCircleIcon as XCircleSolidIcon,
} from '@heroicons/react/24/solid';

// All 12 setup steps in order
const STEP_ORDER = [
  'org_created',
  'email_verified',
  'first_login',
  'campus_added',
  'first_group',
  'second_admin',
  'members_invited',
  'join_codes',
  'first_prayer',
  'first_chat',
  'first_event',
  'first_announcement',
];

const STEP_LABELS = {
  org_created: 'Organization Created',
  email_verified: 'Email Verified',
  first_login: 'First Login',
  campus_added: 'Campus Added',
  first_group: 'First Group Created',
  second_admin: 'Second Admin Invited',
  members_invited: 'Members Invited',
  join_codes: 'Join Codes Active',
  first_prayer: 'First Prayer',
  first_chat: 'First Chat Message',
  first_event: 'First Event',
  first_announcement: 'First Announcement',
};

const STEP_ICONS = {
  org_created: BuildingOffice2Icon,
  email_verified: () => (
    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M21.75 6.75v10.5a2.25 2.25 0 01-2.25 2.25h-15a2.25 2.25 0 01-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25m19.5 0v.243a2.25 2.25 0 01-1.07 1.916l-7.5 4.615a2.25 2.25 0 01-2.36 0L3.32 8.91a2.25 2.25 0 01-1.07-1.916V6.75" />
    </svg>
  ),
  first_login: UserIcon,
  campus_added: MapPinIcon,
  first_group: UsersIcon,
  second_admin: UsersIcon,
  members_invited: UsersIcon,
  join_codes: () => (
    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M13.19 8.688a4.5 4.5 0 011.242 7.244l-4.5 4.5a4.5 4.5 0 01-6.364-6.364l1.757-1.757m9.86-3.172a4.5 4.5 0 00-1.242-7.244l4.5-4.5a4.5 4.5 0 016.364 6.364l-1.757 1.757" />
    </svg>
  ),
  first_prayer: () => (
    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 6.042A8.967 8.967 0 006 3.75c-1.052 0-2.062.18-3 .512v14.25A8.987 8.987 0 016 18c2.305 0 4.408.867 6 2.292m0-14.25a8.966 8.966 0 016-2.292c1.052 0 2.062.18 3 .512v14.25A8.987 8.987 0 0018 18a8.967 8.967 0 00-6 2.292m0-14.25v14.25" />
    </svg>
  ),
  first_chat: () => (
    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M8.625 12a.375.375 0 11-.75 0 .375.375 0 01.75 0zm0 0H8.25m4.125 0a.375.375 0 11-.75 0 .375.375 0 01.75 0zm0 0H12m4.125 0a.375.375 0 11-.75 0 .375.375 0 01.75 0zm0 0h-.375M21 12c0 4.556-4.03 8.25-9 8.25a9.764 9.764 0 01-2.555-.337A5.972 5.972 0 015.41 20.97a5.969 5.969 0 01-.474-.065 4.48 4.48 0 00.978-2.025c.09-.457-.133-.901-.467-1.226C3.93 16.178 3 14.189 3 12c0-4.556 4.03-8.25 9-8.25s9 3.694 9 8.25z" />
    </svg>
  ),
  first_event: CalendarIcon,
  first_announcement: () => (
    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M10.34 15.84c-.688-.06-1.386-.09-2.09-.09H7.5a4.5 4.5 0 110-9h.75c.704 0 1.402-.03 2.09-.09m0 9.18c.253.962.584 1.892.985 2.783.247.55.06 1.21-.463 1.511l-.657.38c-.551.318-1.26.117-1.527-.461a20.845 20.845 0 01-1.44-4.282m3.102.069a18.03 18.03 0 01-.59-4.59c0-1.586.205-3.124.59-4.59m0 9.18a23.848 23.848 0 018.835 2.535M10.34 6.66a23.847 23.847 0 008.835-2.535m0 0A23.74 23.74 0 0018.795 3m.38 1.125a23.91 23.91 0 011.014 5.395m-1.014 8.855c-.118.38-.245.754-.38 1.125m.38-1.125a23.91 23.91 0 001.014-5.395m0-3.46c.495.413.811 1.035.811 1.73 0 .695-.316 1.317-.811 1.73m0-3.46a24.347 24.347 0 010 3.46" />
    </svg>
  ),
};

function formatDate(dateStr) {
  if (!dateStr) return null;
  const d = new Date(dateStr);
  return `${d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'America/Chicago' })} ${d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', timeZone: 'America/Chicago' })}`;
}

export default function OnboardingOrgDetailPage() {
  const { currentEnv } = useEnvironment();
  const { orgId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dataEnv, setDataEnv] = useState(null);

  const fetchData = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await founderDashboardAPI.getOnboardingOrgDetail(orgId);
      setData(response.data);
      setDataEnv(currentEnv);
    } catch (err) {
      console.error('[OnboardingOrgDetailPage] Error:', err);
      const errorData = err.response?.data?.error;
      const errorMsg =
        typeof errorData === 'string'
          ? errorData
          : errorData?.message || err.message || 'Couldn\'t load organization details. Try again?';
      setError(errorMsg);
      setDataEnv(currentEnv);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setData(null);
    setError(null);
    fetchData();
  }, [orgId, currentEnv]);

  // Error state
  if (error && dataEnv === currentEnv) {
    return (
      <div className="space-y-4">
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 text-gray-600 hover:text-gray-900 min-h-[44px]"
        >
          <ArrowLeftIcon className="w-5 h-5" />
          Back
        </button>
        <div className="bg-red-50 border border-red-200 rounded-xl p-6 text-center">
          <p className="text-red-800 font-medium">Couldn't load organization</p>
          <p className="text-red-600 text-sm mt-1">{error}</p>
          <button
            onClick={fetchData}
            className="mt-4 px-6 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors min-h-[44px]"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  const org = data?.org || data?.organization || {};
  const steps = data?.steps || {};
  const features = data?.featureCounts || data?.features || {};
  const admins = data?.admins || org.admins || [];

  // Calculate completed steps — steps are simple booleans { org_created: true }
  const completedSteps = STEP_ORDER.filter(
    (s) => steps[s] === true || steps[s]?.completed || steps[s]?.completedAt
  );
  const completedCount = data?.completedSteps || completedSteps.length;
  const totalSteps = STEP_ORDER.length;
  const progressPercent = Math.round((completedCount / totalSteps) * 100);

  return (
    <div className="space-y-6">
      {/* Breadcrumb + Header */}
      <div className="flex items-center justify-between">
        <div>
          {/* Breadcrumb */}
          <nav className="flex items-center gap-1.5 text-sm text-gray-500 mb-2">
            <button
              onClick={() => navigate('/onboarding')}
              className="hover:text-gray-900 min-h-[44px] flex items-center"
            >
              Onboarding
            </button>
            <ChevronRightIcon className="w-3.5 h-3.5" />
            <span className="text-gray-900 font-medium">
              {loading ? 'Loading...' : org.name || 'Organization Detail'}
            </span>
          </nav>

          {/* Back button */}
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-2 min-h-[44px]"
          >
            <ArrowLeftIcon className="w-4 h-4" />
            <span className="text-sm">Back</span>
          </button>

          {loading ? (
            <div className="h-8 w-48 bg-gray-200 rounded animate-pulse" />
          ) : (
            <h2 className="text-2xl font-bold text-gray-900">
              {org.name || 'Unnamed Organization'}
            </h2>
          )}
        </div>
        <RefreshIndicator onRefresh={fetchData} loading={loading} />
      </div>

      {/* Section 1: Org Profile Card */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
        {loading ? (
          <div className="animate-pulse space-y-4">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 bg-gray-200 rounded-full" />
              <div className="space-y-2 flex-1">
                <div className="h-6 bg-gray-200 rounded w-1/3" />
                <div className="h-4 bg-gray-200 rounded w-1/4" />
              </div>
            </div>
          </div>
        ) : (
          <>
            <div className="flex items-start gap-4 mb-6">
              <div className="w-14 h-14 rounded-full bg-indigo-100 flex items-center justify-center flex-shrink-0">
                <BuildingOffice2Icon className="w-7 h-7 text-indigo-600" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-3 flex-wrap">
                  <h3 className="text-xl font-semibold text-gray-900">
                    {org.name || 'Unnamed'}
                  </h3>
                  {(org.subscriptionTier || org.tier) && (
                    <span className="px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-700 capitalize">
                      {(org.subscriptionTier || org.tier)}
                    </span>
                  )}
                </div>
                {(org.city || org.state) && (
                  <div className="flex items-center gap-1.5 mt-1 text-gray-500">
                    <MapPinIcon className="w-4 h-4" />
                    <span>{[org.city, org.state].filter(Boolean).join(', ')}</span>
                  </div>
                )}
              </div>
              <Link
                to={`/founder-dashboard/organizations/${orgId}`}
                className="flex items-center gap-1.5 text-sm text-blue-600 hover:text-blue-800 min-h-[44px] px-3"
              >
                <span>Full Detail</span>
                <ArrowTopRightOnSquareIcon className="w-4 h-4" />
              </Link>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                <CalendarIcon className="w-5 h-5 text-gray-400 flex-shrink-0" />
                <div>
                  <p className="text-xs text-gray-500">Created</p>
                  <p className="text-sm font-medium text-gray-900">
                    {formatDate(org.createdAt) || '-'}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                <UsersIcon className="w-5 h-5 text-gray-400 flex-shrink-0" />
                <div>
                  <p className="text-xs text-gray-500">Members</p>
                  <p className="text-sm font-medium text-gray-900">
                    {features.members ?? org.memberCount ?? 0}
                  </p>
                </div>
              </div>
              {admins.length > 0 && (
                <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg sm:col-span-2">
                  <UserIcon className="w-5 h-5 text-gray-400 flex-shrink-0" />
                  <div>
                    <p className="text-xs text-gray-500">Admins</p>
                    <p className="text-sm font-medium text-gray-900">
                      {admins.map((a) => a.name || a.email).join(', ')}
                    </p>
                  </div>
                </div>
              )}
            </div>
          </>
        )}
      </div>

      {/* Section 2: 12-Step Progress Checklist */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-800">Setup Progress</h3>
          {!loading && (
            <span className="text-sm font-medium text-gray-500">
              {completedCount}/{totalSteps} completed
            </span>
          )}
        </div>

        {loading ? (
          <div className="animate-pulse">
            <div className="h-3 bg-gray-200 rounded-full w-full mb-6" />
            <div className="space-y-3">
              {[1, 2, 3, 4, 5, 6].map((i) => (
                <div key={i} className="h-10 bg-gray-100 rounded w-full" />
              ))}
            </div>
          </div>
        ) : (
          <>
            {/* Progress bar */}
            <div className="mb-6">
              <div className="w-full bg-gray-200 rounded-full h-3">
                <div
                  className={`h-3 rounded-full transition-all duration-500 ${
                    progressPercent === 100 ? 'bg-green-500' : progressPercent >= 50 ? 'bg-blue-500' : 'bg-amber-500'
                  }`}
                  style={{ width: `${progressPercent}%` }}
                />
              </div>
              <p className="text-xs text-gray-500 mt-1 text-right">
                {progressPercent}% complete
              </p>
            </div>

            {/* Checklist */}
            <div className="space-y-1">
              {STEP_ORDER.map((stepKey) => {
                const stepVal = steps[stepKey];
                const isCompleted = stepVal === true || stepVal?.completed || stepVal?.completedAt;
                const completedDate = typeof stepVal === 'object' ? (stepVal?.completedAt || stepVal?.date) : null;
                const StepIcon = STEP_ICONS[stepKey] || BuildingOffice2Icon;

                return (
                  <div
                    key={stepKey}
                    className={`flex items-center gap-3 px-3 py-2.5 rounded-lg ${
                      isCompleted ? 'bg-green-50' : 'bg-gray-50'
                    }`}
                  >
                    {/* Checkmark / Circle */}
                    {isCompleted ? (
                      <CheckCircleSolidIcon className="w-5 h-5 text-green-500 flex-shrink-0" />
                    ) : (
                      <div className="w-5 h-5 rounded-full border-2 border-gray-300 flex-shrink-0" />
                    )}

                    {/* Icon + Label */}
                    <div className="flex items-center gap-2 flex-1 min-w-0">
                      <span className={isCompleted ? 'text-green-600' : 'text-gray-400'}>
                        <StepIcon className="w-4 h-4" />
                      </span>
                      <span
                        className={`text-sm ${
                          isCompleted
                            ? 'text-green-800 font-medium'
                            : 'text-gray-500'
                        }`}
                      >
                        {STEP_LABELS[stepKey]}
                      </span>
                    </div>

                    {/* Date / "Not yet" */}
                    <span
                      className={`text-xs flex-shrink-0 ${
                        isCompleted ? 'text-green-600' : 'text-gray-400'
                      }`}
                    >
                      {isCompleted && completedDate
                        ? formatDate(completedDate)
                        : isCompleted
                        ? 'Done'
                        : 'Not yet'}
                    </span>
                  </div>
                );
              })}
            </div>
          </>
        )}
      </div>

      {/* Section 3: Community Feature Adoption */}
      <div>
        <h3 className="text-lg font-semibold text-gray-800 mb-4">Community Feature Adoption</h3>

        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 animate-pulse">
                <div className="h-4 bg-gray-200 rounded w-1/2 mb-3" />
                <div className="h-8 bg-gray-200 rounded w-3/4 mb-2" />
                <div className="h-3 bg-gray-200 rounded w-1/3" />
              </div>
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <FeatureAdoptionCard
              title="Prayers"
              count={features.prayerCount ?? features.prayers ?? 0}
              firstUsed={features.firstPrayerAt || features.firstPrayer}
              icon="prayer"
            />
            <FeatureAdoptionCard
              title="Chat Messages"
              count={features.chatCount ?? features.chatMessages ?? 0}
              firstUsed={features.firstChatAt || features.firstChat}
              icon="chat"
            />
            <FeatureAdoptionCard
              title="Events"
              count={features.eventCount ?? features.events ?? 0}
              firstUsed={features.firstEventAt || features.firstEvent}
              icon="event"
            />
            <FeatureAdoptionCard
              title="Announcements"
              count={features.announcementCount ?? features.announcements ?? 0}
              firstUsed={features.firstAnnouncementAt || features.firstAnnouncement}
              icon="announcement"
            />
          </div>
        )}
      </div>
    </div>
  );
}

// Local helper component for feature adoption cards
function FeatureAdoptionCard({ title, count, firstUsed, icon }) {
  const iconColors = {
    prayer: 'bg-purple-50 text-purple-600',
    chat: 'bg-blue-50 text-blue-600',
    event: 'bg-green-50 text-green-600',
    announcement: 'bg-amber-50 text-amber-600',
  };

  const colorClass = iconColors[icon] || 'bg-gray-50 text-gray-600';

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 hover:shadow-md transition-shadow">
      <div className="flex items-start justify-between mb-3">
        <p className="text-sm text-gray-500 uppercase tracking-wide font-medium">
          {title}
        </p>
        <div className={`p-2 rounded-lg ${colorClass}`}>
          {icon === 'prayer' && (
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 6.042A8.967 8.967 0 006 3.75c-1.052 0-2.062.18-3 .512v14.25A8.987 8.987 0 016 18c2.305 0 4.408.867 6 2.292m0-14.25a8.966 8.966 0 016-2.292c1.052 0 2.062.18 3 .512v14.25A8.987 8.987 0 0018 18a8.967 8.967 0 00-6 2.292m0-14.25v14.25" />
            </svg>
          )}
          {icon === 'chat' && (
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M8.625 12a.375.375 0 11-.75 0 .375.375 0 01.75 0zm0 0H8.25m4.125 0a.375.375 0 11-.75 0 .375.375 0 01.75 0zm0 0H12m4.125 0a.375.375 0 11-.75 0 .375.375 0 01.75 0zm0 0h-.375M21 12c0 4.556-4.03 8.25-9 8.25a9.764 9.764 0 01-2.555-.337A5.972 5.972 0 015.41 20.97a5.969 5.969 0 01-.474-.065 4.48 4.48 0 00.978-2.025c.09-.457-.133-.901-.467-1.226C3.93 16.178 3 14.189 3 12c0-4.556 4.03-8.25 9-8.25s9 3.694 9 8.25z" />
            </svg>
          )}
          {icon === 'event' && (
            <CalendarIcon className="w-5 h-5" />
          )}
          {icon === 'announcement' && (
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M10.34 15.84c-.688-.06-1.386-.09-2.09-.09H7.5a4.5 4.5 0 110-9h.75c.704 0 1.402-.03 2.09-.09m0 9.18c.253.962.584 1.892.985 2.783.247.55.06 1.21-.463 1.511l-.657.38c-.551.318-1.26.117-1.527-.461a20.845 20.845 0 01-1.44-4.282m3.102.069a18.03 18.03 0 01-.59-4.59c0-1.586.205-3.124.59-4.59m0 9.18a23.848 23.848 0 018.835 2.535M10.34 6.66a23.847 23.847 0 008.835-2.535m0 0A23.74 23.74 0 0018.795 3m.38 1.125a23.91 23.91 0 011.014 5.395m-1.014 8.855c-.118.38-.245.754-.38 1.125m.38-1.125a23.91 23.91 0 001.014-5.395m0-3.46c.495.413.811 1.035.811 1.73 0 .695-.316 1.317-.811 1.73m0-3.46a24.347 24.347 0 010 3.46" />
            </svg>
          )}
        </div>
      </div>
      <p className="text-3xl font-bold text-gray-900">{count}</p>
      <p className="text-xs text-gray-400 mt-1">
        {firstUsed ? `First: ${formatDate(firstUsed)}` : 'Not yet used'}
      </p>
    </div>
  );
}
